/**
 * Remove keys whose value is `undefined`, recursively.
 *
 * The Firebase callable SDK does NOT drop them the way JSON.stringify does —
 * `encode()` turns any nullish value into a literal `null` and sends it. So
 * `{ radiusMeters: undefined }`, which every developer reads as "field not
 * set", arrives at the server as `{ radiusMeters: null }` and a
 * `z.number().optional()` rejects it with "Expected number, received null".
 *
 * Stripping here fixes it once for every callable instead of making each of
 * ~25 optional-number schemas null-tolerant and remembering it forever. An
 * explicit `null` is left alone: that is a caller deliberately clearing a
 * value, and some schemas rely on telling the two apart.
 */
export function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefined(v)) as unknown as T;
  }
  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return value;
}
